import React, { useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useRequest } from "@components/AppHook";
import VkVideoStatus from "@components/Common/VKVideo/VKVideoStatus";
import { DevForm } from "@components/Forms/Fields";
import { Awesome } from "@components/Helpers";
import { arrayMove, url_reverse } from "@components/Utils";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ListItemImage from "@screens/Game/Highlights/components/ListItemImage";
import TypeBadge from "@screens/Game/Highlights/components/TypeBadge";

const SortableItem = ({ item, idsToDelete, deleteToggleHandle, sortingId }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id,
    });

    const style = {
        opacity: isDragging ? 0.4 : undefined,
        transform: CSS.Translate.toString(transform),
        transition,
    };

    return (
        <tr ref={setNodeRef} style={style}>
            <td {...listeners} {...attributes}>
                <div style={{ cursor: "grab" }}>
                    {item.id === sortingId ? (
                        <Spinner animation="border" variant="primary" size="sm" />
                    ) : (
                        <Awesome type="bars" />
                    )}
                </div>
            </td>
            <td>
                <ListItemImage data={item} />
            </td>
            <td>
                <TypeBadge data={item} />
                <br />
                <VkVideoStatus data={item.vkvideo_data} />
            </td>
            <td>
                <DevForm.Checkbox
                    checked={idsToDelete.includes(item.id)}
                    value={item.id}
                    onChange={deleteToggleHandle}
                />
            </td>
        </tr>
    );
};

const SortableList = ({ apiData, gameId, locl, idsToDelete, deleteToggleHandle, sortingId }) => {
    const hlData = apiData?.hl_data?.object_list[locl];
    return (
        <tbody>
            <SortableContext items={hlData} strategy={verticalListSortingStrategy}>
                {hlData.map((item) => (
                    <SortableItem
                        key={`item-${item.id}`}
                        item={item}
                        gameId={gameId}
                        locl={locl}
                        idsToDelete={idsToDelete}
                        deleteToggleHandle={deleteToggleHandle}
                        sortingId={sortingId}
                    />
                ))}
            </SortableContext>
        </tbody>
    );
};

function List({ apiData, setApiData, gameId, locl, idsToDelete, deleteToggleHandle }) {
    const { t } = useTranslation();
    const { api_post } = useRequest();
    const [sortingId, setSortingId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor));

    const onDragEndHandle = (event) => {
        const { active, over } = event;
        const hlData = apiData.hl_data.object_list[locl];

        if (active.id !== over.id) {
            const oldIndex = hlData.findIndex((item) => item.id === active.id);
            const newIndex = hlData.findIndex((item) => item.id === over.id);
            const new_list = arrayMove(hlData, oldIndex, newIndex);
            const ids = new_list.map((o) => o.id);

            const newApiData = { ...apiData };
            newApiData.hl_data.object_list[locl] = new_list;
            setApiData(newApiData);

            setSortingId(active.id);

            api_post(null, url_reverse("api:game:highlights:list", { game_id: gameId, locl }), {
                params: {
                    action: "rearrange",
                    ids,
                },
                finally: () => setSortingId(null),
            });
        }
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndHandle}>
            <Table>
                <thead>
                    <tr>
                        <th width="5%" />
                        <th width="20%">{t("Изображение")}</th>
                        <th width="70%">{t("Тип")}</th>
                        <th />
                    </tr>
                </thead>
                <SortableList
                    sortingId={sortingId}
                    apiData={apiData}
                    gameId={gameId}
                    locl={locl}
                    idsToDelete={idsToDelete}
                    deleteToggleHandle={deleteToggleHandle}
                />
            </Table>
        </DndContext>
    );
}

export default List;
